import { STATUS_COLORS } from "@/pages/workOrders/components/statusConstants";
import type { CalendarEvent } from "@/shared/repositories/CalendarEventRepository";
import type { CalendarWorkOrder } from "@/shared/repositories/WorkOrderRepository";
import AlarmIcon from "@mui/icons-material/Alarm";
import SyncIcon from "@mui/icons-material/Sync";
import { Box, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { memo } from "react";
import {
	eventDisplayName,
	getEventType,
	isSpellBreak,
	stripedFill,
} from "../config/eventTypes";
import {
	formatDuration,
	formatDurationHuman,
	formatTimeRange,
	isPlainLeft,
	type SegmentItem,
	segmentGeometry,
	type WeekSegment,
} from "../utils/calendarUtils";
import { CarryIcon, carryNote } from "./CarryMarker";

export type CalendarItem = SegmentItem &
	(
		| { kind: "wo"; event: CalendarEvent; wo: CalendarWorkOrder }
		| { kind: "other"; event: CalendarEvent }
	);

// Wide enough for the longest part numbers; identifiers never wrap mid-value
const TOOLTIP_SLOT_PROPS = { tooltip: { sx: { maxWidth: 420 } } };
const TOOLTIP_ID_SX = { fontWeight: 600, whiteSpace: "nowrap" };

export const EVENT_ROW_HEIGHT = 40;
const COMPACT_HEIGHT = 22;
/** Day cell padding. Bars are sized in % of the padded box, so every day they
 *  cross owes them the padding on both sides plus the cell's 1px border. */
export const DAY_CELL_PADDING = 6;
const DAY_CELL_GAP = 2 * DAY_CELL_PADDING + 1;

export const itemColors = (item: CalendarItem) => {
	if (item.kind === "wo")
		return STATUS_COLORS[item.wo.Status] ?? STATUS_COLORS.Pending;
	const type = getEventType(item.event.EventTypeCode);
	return { bg: type.bg, color: type.color };
};

export const itemLines = (item: CalendarItem): [string, string] => {
	if (item.kind === "wo")
		return [
			item.wo.PartNumber ?? "—",
			`×${item.wo.Quantity} · ${item.wo.Status}`,
		];
	return [
		eventDisplayName(item.event),
		formatDurationHuman(item.start, item.end),
	];
};

export const isItemDraggable = (item: CalendarItem) =>
	item.kind === "wo" ? item.wo.Status === "Pending" : true;

type Props = {
	seg: WeekSegment<CalendarItem>;
	rowIndex: number;
	isDragging: boolean;
	draggedKey: string | null;
	/** any segment of this item is under the pointer: a work order spanning
	 *  weeks lights up on every row it touches */
	isHovered: boolean;
	onHover: (key: string | null) => void;
	onItemMouseDown: (e: React.MouseEvent, item: CalendarItem) => void;
	onItemContextMenu: (e: React.MouseEvent, item: CalendarItem) => void;
	onWorkOrderClick: (wo: CalendarWorkOrder) => void;
	isMenuTarget: boolean;
};

// Memoized: a drag re-renders MonthGrid on every animation frame, and without
// this every bar (each with its Tooltip) re-renders with it.
export const EventBar = memo(function EventBar({
	seg,
	rowIndex,
	isDragging,
	draggedKey,
	isHovered,
	onHover,
	onItemMouseDown,
	onItemContextMenu,
	onWorkOrderClick,
	isMenuTarget,
}: Props) {
	const item = seg.item;
	const isWo = item.kind === "wo";
	const compact = item.compact === true;
	const spell = item.kind === "other" && isSpellBreak(item.event);
	const colors = itemColors(item);
	const draggable = isItemDraggable(item);
	const [line1, line2] = itemLines(item);

	const { startPercent, widthPercent } = segmentGeometry(seg);
	// A bar that continues on another week row runs to the cell edge
	const bleedLeft = seg.isStart ? 0 : DAY_CELL_PADDING;
	const bleedRight = seg.isEnd ? 0 : DAY_CELL_PADDING;
	const extraWidth = (seg.spanDays - 1) * DAY_CELL_GAP + bleedLeft + bleedRight;
	const highlight = {
		filter: "brightness(1.05)",
		boxShadow: `0 0 0 1.5px ${colors.color}, 0 2px 6px #0000002E`,
		zIndex: 30,
	};
	const hoverLook = {
		...highlight,
		minWidth: "max-content",
		"& .label": { display: "inline" },
	};
	const note = isWo ? carryNote(item.event) : null;

	const tooltip = (
		<Box>
			{isWo && (
				<Typography variant="body2" sx={TOOLTIP_ID_SX}>
					Work Order # {item.wo.WorkOrderNumber ?? "—"}
				</Typography>
			)}
			<Typography variant="body2" sx={TOOLTIP_ID_SX}>
				{isWo
					? (item.wo.PartNumber ?? "Work Order")
					: eventDisplayName(item.event)}
			</Typography>
			{isWo ? (
				<Typography variant="caption" display="block">
					{item.wo.Status} • {item.wo.Quantity} pcs • shift{" "}
					{item.wo.DisplayShift}
				</Typography>
			) : (
				item.event.Comments && (
					<Typography variant="caption" display="block">
						{item.event.Comments}
					</Typography>
				)
			)}
			{note && (
				<Typography variant="caption" display="block">
					{note}
				</Typography>
			)}
			{isWo && item.wo.Notes && (
				<Typography variant="caption" display="block">
					{item.wo.Notes}
				</Typography>
			)}
			<Typography variant="caption" display="block">
				{formatTimeRange(item.start, item.end)}
			</Typography>
			<Typography variant="caption" display="block">
				Duration: {formatDuration(item.start, item.end)}
			</Typography>
		</Box>
	);

	const CompactIcon = spell ? SyncIcon : AlarmIcon;

	return (
		<Tooltip
			// "" = no tooltip; MUI ignores a late `open`
			title={isDragging ? "" : tooltip}
			slotProps={TOOLTIP_SLOT_PROPS}
		>
			<Box
				data-item-key={item.key}
				onMouseEnter={() => onHover(item.key)}
				onMouseLeave={() => onHover(null)}
				onMouseDown={draggable ? (e) => onItemMouseDown(e, item) : undefined}
				onClick={(e) => {
					e.stopPropagation();
					if (isWo && !draggable && isPlainLeft(e)) onWorkOrderClick(item.wo);
				}}
				// stops here: no day menu underneath
				onContextMenu={(e) => onItemContextMenu(e, item)}
				sx={{
					position: "absolute",
					top:
						rowIndex * EVENT_ROW_HEIGHT +
						(compact ? (EVENT_ROW_HEIGHT - COMPACT_HEIGHT) / 2 : 0),
					left: 0,
					marginLeft: `calc(${startPercent}% - ${bleedLeft}px)`,
					width: `calc(${widthPercent}% + ${extraWidth}px)`,
					// never narrower than the icon plus its padding: a 15-min slot is ~2px
					minWidth: compact ? "max-content" : undefined,
					height: compact ? COMPACT_HEIGHT : EVENT_ROW_HEIGHT - 2,
					boxSizing: "border-box",
					zIndex: 1,
					padding: compact ? "0 3px" : "3px 6px",
					display: compact ? "flex" : undefined,
					alignItems: "center",
					gap: 0.5,
					overflow: "visible",
					backgroundColor: colors.bg,
					...(spell &&
						(compact
							? stripedFill(colors.color, 3, 2)
							: stripedFill(colors.color))),
					color: colors.color,
					opacity: draggedKey === item.key ? 0.5 : 1,
					borderRadius:
						seg.isStart && seg.isEnd
							? "4px"
							: seg.isStart
								? "4px 0 0 4px"
								: seg.isEnd
									? "0 4px 4px 0"
									: 0,
					border: `1px solid ${alpha(colors.color, 0.35)}`,
					borderLeft: seg.isStart
						? `${compact ? 1 : 4}px solid ${colors.color}`
						: "none",
					...(!seg.isEnd && { borderRight: "none" }),
					cursor: draggable ? "move" : "pointer",
					"& .label": compact ? { display: "none" } : undefined,
					...(isHovered && !isDragging && highlight),
					...(isMenuTarget && hoverLook),
					// only the segment under the pointer grows to fit its label
					"&:hover": isDragging ? undefined : hoverLook,
					transition: "filter 0.15s ease, box-shadow 0.15s ease",
				}}
			>
				{compact ? (
					<>
						<CompactIcon sx={{ fontSize: 13 }} />
						<Box
							component="span"
							className="label"
							sx={{
								fontWeight: 600,
								fontSize: "0.75rem",
								whiteSpace: "nowrap",
							}}
						>
							{line1} · {formatTimeRange(item.start, item.end)}
						</Box>
					</>
				) : (
					<>
						<Box
							sx={{
								fontWeight: 700,
								fontSize: "0.72rem",
								lineHeight: "17px",
								whiteSpace: "nowrap",
							}}
						>
							{isWo && <CarryIcon event={item.event} />}
							{line1}
						</Box>
						<Box
							sx={{
								fontWeight: 500,
								fontSize: "0.68rem",
								lineHeight: "15px",
								opacity: 0.85,
								whiteSpace: "nowrap",
							}}
						>
							{line2}
						</Box>
					</>
				)}
			</Box>
		</Tooltip>
	);
});
